import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Col, Dialog, MessagePlugin, Row, Select, Tree } from 'tdesign-react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { useMemoizedFn } from 'ahooks';
import Form from '@/components/Form';
import { isVoid, sleep, toTsOptions } from '@/utils/common';
import Descriptions from '@/components/Descriptions';
import {
  fetchRoleList,
  fetchNamespace,
  fetchKafkaTopics,
  resetOptions,
  fetchClusterType,
} from '@/slices/organizeSlice';
import { getGroupDetail, manageResource } from '@/services/organizeService';

const { FormItem } = Form;

// 资源类型，除了kafka，其他都是namespace
function getResourceType(clusterType) {
  if (clusterType === 'kafka') {
    return 'topic';
  }
  return 'namespace';
}

function GrantModal({
  visible,
  handleClose,
  groupId,
  clusterList,
  fetchRoleList,
  roleList,
  fetchNamespace,
  namespaceTree,
  kafkaTopicList,
  resetOptions,
  fetchKafkaTopics,
  clusterTypeOptions,
  fetchClusterType,
  loading,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const clusterType = Form.useWatch('clusterType', form);
  const clusterName = Form.useWatch('clusterName', form);

  const clusterOptions = useMemo(() => {
    return clusterList?.filter((item) => item.clusterType === clusterType);
  }, [clusterType, clusterList]);

  const onSubmit = async (values) => {
    const { clusterName, clusterType, resources = [], tokens = [] } = values;
    const resourceType = getResourceType(clusterType);
    const params = {
      groupId,
      clusterName,
      clusterType,
      resources: [
        ...resources.map((item) => ({
          clusterName,
          clusterType,
          resourceType,
          resourceName: item,
        })),
        ...tokens.map((item) => ({
          clusterName,
          clusterType,
          resourceType: 'role',
          resourceName: item,
        })),
      ],
    };

    try {
      setSubmitting(true);
      await manageResource(params);
      MessagePlugin.success('操作成功');
      handleClose();
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const loadDetail = async ({ clusterType, clusterName }) => {
    const { data } = await getGroupDetail(groupId);
    const resources = [];
    const tokens = [];
    const resourceType = getResourceType(clusterType);
    data.forEach((item) => {
      if (
        item.resourceType === resourceType &&
        item.clusterType === clusterType &&
        item.clusterName === clusterName
      ) {
        resources.push(item.resourceName);
      }
      if (
        item.resourceType === 'role' &&
        item.clusterType === clusterType &&
        item.clusterName === clusterName
      ) {
        tokens.push(item.resourceName);
      }
    });
    form.setFieldsValue({ resources, tokens });
  };

  // 集群类型发生变化
  const clusterTypeChange = useMemoizedFn(async () => {
    resetOptions();
    form.resetFields(['clusterName', 'resources', 'tokens']);
    // 先保证清空，防止同名无效
    await sleep(300);
    // 设置默认集群
    form.setFieldValue(
      'clusterName',
      clusterList.find((item) => item.clusterType === clusterType)?.value,
    );
  });

  const fetchAllData = async (clusterType, clusterName) => {
    if (clusterType === 'kafka') {
      await fetchKafkaTopics({ clusterName, clusterType });
    } else {
      await fetchNamespace({ clusterName, clusterType });
    }
    await fetchRoleList({ clusterName, clusterType });
    loadDetail({ clusterType, clusterName });
  };

  const clusterChange = useMemoizedFn(async (value) => {
    form.resetFields(['resources', 'tokens']);
    fetchAllData(clusterType, value);
  });

  useEffect(() => {
    if (visible) {
      fetchClusterType();
    } else {
      form.resetFields();
      resetOptions();
    }
  }, [visible, resetOptions, form, fetchClusterType]);

  // 选择默认集群类型
  useEffect(() => {
    if (!isEmpty(clusterTypeOptions)) {
      form.setFieldsValue({ clusterType: clusterTypeOptions[0].value });
    }
  }, [clusterTypeOptions, form]);

  useEffect(() => {
    clusterTypeChange();
  }, [clusterType, clusterTypeChange]);

  useEffect(() => {
    if (!isVoid(clusterName)) {
      clusterChange(clusterName);
    }
  }, [clusterName, clusterChange]);

  return (
    <Dialog
      header='授权'
      visible={visible}
      onClose={() => handleClose()}
      destroyOnClose
      placement='center'
      confirmBtn={{ loading: submitting, content: '确定' }}
      onConfirm={() => form.submit()}
      width='60%'
    >
      <Form form={form} onFinish={onSubmit} style={{ width: '95%' }}>
        <Row gutter={30}>
          <Col span={6}>
            <FormItem
              label='集群类别'
              name='clusterType'
              labelWidth={80}
              rules={[{ required: true }]}
            >
              <Select options={toTsOptions(clusterTypeOptions)} />
            </FormItem>
          </Col>
          <Col span={6}>
            <FormItem
              label='选择集群'
              name='clusterName'
              labelWidth={80}
              rules={[{ required: true }]}
            >
              <Select filterable options={toTsOptions(clusterOptions)} />
            </FormItem>
          </Col>
        </Row>
        <Descriptions title='资源授权' bordered style={{ margin: '12px 0' }}>
          <FormItem name='resources' style={{ width: '100%' }}>
            <Tree
              scroll={{ type: 'virtual' }}
              style={{ height: '300px', flex: 1 }}
              data={clusterType === 'kafka' ? kafkaTopicList : namespaceTree}
              checkable
              empty={loading ? '加载中...' : '暂无数据'}
            />
          </FormItem>
        </Descriptions>
        <Descriptions title='Acl授权' bordered>
          <FormItem name='tokens'>
            {isEmpty(roleList) ? (
              loading ? (
                '加载中...'
              ) : (
                '暂无数据'
              )
            ) : (
              <Checkbox.Group options={roleList} />
            )}
          </FormItem>
        </Descriptions>
      </Form>
    </Dialog>
  );
}

export default connect(
  (state) => ({
    clusterList: state.organize.clusterList,
    roleList: state.organize.roleList,
    namespaceTree: state.organize.namespaceTree,
    kafkaTopicList: state.organize.kafkaTopicList,
    clusterTypeOptions: state.organize.clusterTypeOptions,
    loading: state.organize.loading,
  }),
  {
    fetchRoleList,
    fetchNamespace,
    resetOptions,
    fetchKafkaTopics,
    fetchClusterType,
  },
)(GrantModal);
